import React, { Fragment, useEffect, useMemo, useState } from 'react'

import { GridRow, GridColumn, Grid } from 'semantic-ui-react'
import Divider from '../../../Divider'
import Loading from 'components/Loading'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { parseMedications } from '@enlyt/helpers/parse'

import useMediaQuery from 'hooks/useMediaQuery'

import themeStyles from './styles'
import { useTheme } from 'contexts/ThemeProvider'
import localization from './localization'
import NoData from '../../../MyHealthContentArea/NoDataAvailableBar'
import { sortMedications } from '@web/helpers/sortBy'
import FhirEnhancedTypes from '@web/stores/FhirEnhanced/Actions'
import { useDispatch, useSelector } from 'react-redux'
import SthWentWrong from 'containers/MyHealth/components/MyHealthContentArea/SthWentWrong'

import Button from '@web/components/LoadMoreButton'
import withLocalization from '@web/helpers/localizationUtils'
import LearnMoreButton from '@web/components/HealthLibraryLearnMore'
import {
  healthLibrary,
  healthLibraryLoading
} from '@web/stores/FhirEnhanced/Select'
import { parseHealthLibrary } from '@web/helpers/parseEnhanced'
import { useTranslation } from 'react-i18next'
import { HEALTH_LIBRARY_MAPPING } from '@web/helpers/sources'

const MedicationItem = item => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const { isShowDivider = false, dispatch, t } = item
  const { isMobile } = useMediaQuery()
  const healthArticles = useSelector(healthLibrary)
  const [healthArticleData, setHealthArticleData] = useState([])
  const [currentArticleCode, setCurrentArticleCode] = useState('')
  const healthArticlesLoading = useSelector(healthLibraryLoading)
  const { i18n } = useTranslation()
  const language = i18n.language === 'es' ? 'es' : 'en'

  useEffect(
    () => {
      if (!healthArticlesLoading?.[currentArticleCode]) {
        const healthArticleData = _.get(
          healthArticles?.[language],
          currentArticleCode,
          []
        )
        const parsedData = parseHealthLibrary(healthArticleData)
        setHealthArticleData(parsedData)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [healthArticlesLoading?.[currentArticleCode]]
  )

  const openLearnMorePopup = ({ terms }) => {
    const key = terms
    const healthArticleData = _.get(healthArticles?.[language], key, [])
    setCurrentArticleCode(key)
    if (!_.isEmpty(healthArticleData)) {
      const parsedData = parseHealthLibrary(healthArticleData)
      setHealthArticleData(parsedData)
    } else if (!healthArticlesLoading?.[key]) {
      dispatch(
        FhirEnhancedTypes.healthLibrarySearch(
          language,
          terms,
          undefined,
          HEALTH_LIBRARY_MAPPING.MEDICATION
        )
      )
    }
  }
  const handleCloseLearnMorePopup = () => {
    setHealthArticleData([])
  }
  return (
    <div className='item-container'>
      {!isMobile && (
        <>
          <Grid>
            <GridRow>
              <GridColumn width={4} />
              <GridColumn width={2}>
                <div style={styles.fieldName}>{t('startedOnUppercase')}</div>
              </GridColumn>
              <GridColumn width={3}>
                <div style={styles.fieldName}>{t('orderedByUppercase')}</div>
              </GridColumn>
              <GridColumn width={7}>
                <div style={styles.fieldName}>{t('instructionsUppercase')}</div>
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid>
            <GridRow style={styles.noPaddingTop}>
              <GridColumn width={4}>
                <div style={styles.itemName}>{_.get(item, 'title')}</div>
                <LearnMoreButton
                  data={healthArticleData}
                  onClick={() => openLearnMorePopup({ terms: item?.title })}
                  onClose={handleCloseLearnMorePopup}
                  shouldLoading={healthArticlesLoading?.[currentArticleCode]}
                  mapKey={item?.title}
                  buttonStyle={styles.learnMoreButton}
                  language={language}
                />
              </GridColumn>
              <GridColumn width={2}>
                <div style={styles.itemContent}>
                  {_.get(item, 'authoredOn')}
                </div>
              </GridColumn>
              <GridColumn width={3}>
                <div style={styles.itemContent}>{_.get(item, 'requester')}</div>
              </GridColumn>
              <GridColumn width={7}>
                <div style={styles.itemContent}>
                  {_.get(item, 'patientInstruction')}
                </div>
              </GridColumn>
            </GridRow>
          </Grid>
        </>
      )}
      {isMobile && (
        <>
          <Grid>
            <GridRow>
              <GridColumn width={8} />
              <GridColumn width={4} style={styles.noPadding}>
                <div style={styles.fieldName}>{t('startedOnUppercase')}</div>
              </GridColumn>
              <GridColumn width={4}>
                <div style={styles.fieldName}>{t('orderedByUppercase')}</div>
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid>
            <GridRow style={styles.noPaddingTop}>
              <GridColumn width={8}>
                <div style={styles.itemName}>{_.get(item, 'title')}</div>
              </GridColumn>
              <GridColumn width={4} style={styles.noPadding}>
                <div style={styles.itemContent}>
                  {_.get(item, 'authoredOn')}
                </div>
              </GridColumn>
              <GridColumn width={4}>
                <div style={styles.itemContent}>{_.get(item, 'requester')}</div>
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid>
            <GridRow style={styles.learnMoreContainer}>
              <GridColumn width={16}>
                <LearnMoreButton
                  data={healthArticleData}
                  onClick={() => openLearnMorePopup({ terms: item?.title })}
                  onClose={handleCloseLearnMorePopup}
                  shouldLoading={healthArticlesLoading?.[currentArticleCode]}
                  mapKey={item?.title}
                  buttonStyle={styles.learnMoreButton}
                  language={language}
                />
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid>
            <GridRow>
              <GridColumn width={16}>
                <div style={styles.fieldName}>{t('instructionsUppercase')}</div>
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid>
            <GridRow style={styles.noPaddingTop}>
              <GridColumn width={16}>
                <div style={styles.itemContent}>
                  {_.get(item, 'patientInstruction')}
                </div>
              </GridColumn>
            </GridRow>
          </Grid>
        </>
      )}
      {isShowDivider && <Divider />}
    </div>
  )
}

const Medications = ({ loading, data, name, error, t }) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const items = useMemo(() => parseMedications(sortMedications(data)), [data])
  const dispatch = useDispatch()
  const nextUrl = _.filter(data?.link, item => item.relation === 'next')[0]?.url
  const loadMoreOnClick = () => {
    dispatch(FhirEnhancedTypes.medicationRequestSearchLoadMore(nextUrl))
  }
  return (
    <>
      <div style={styles.container}>
        {loading && _.isEmpty(items) && <Loading />}
        {!_.isEmpty(items) &&
          items.map((item, index) => (
            <MedicationItem
              key={uuidv4()}
              {...item}
              isShowDivider={items.length - index > 1}
              dispatch={dispatch}
              t={t}
            />
          ))}
        {_.isEmpty(items) &&
          !loading &&
          (error ? <SthWentWrong /> : <NoData name={name} />)}
      </div>
      {!_.isEmpty(items) && nextUrl && (
        <Button onClick={loadMoreOnClick} loading={loading} />
      )}
    </>
  )
}

export default withLocalization(Medications, localization)
